import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Checkbox,
    AcceptIcon,
    SendIcon,
    CloseIcon,
    Divider,
    ProviderConsumer,
} from "@fluentui/react-northstar";
import SlateEditor from "../../Wysiwyg/SlateEditor";
import { editorPlainText, markdownToSlate, withoutTrailingEmptyLines } from "../../Wysiwyg/slate-editor-helpers";
import { slateToComment } from "../commentSaving";
import { getFileIcon } from "../../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent";
import EditableTicketAttachment, { EditTicketAttachmentsProps } from "./EditableTicketAttachment";
import { useFileDrop } from "./useFileDrop";
import { START_LINES } from "../commentBoxHeight";
import AppCSS from "../TicketConversation.module.css";

const imagePreviewExtensions = new Set(
    "jpg gif png jpeg bmp ico tif tiff psd ps ai".split(" ")
);

const isImageFile = (file: File) => {
    if (file.type.startsWith("image/")) return true;
    const m = /\.([^.]+)$/.exec(file.name);
    return m ? imagePreviewExtensions.has(m[1].toLowerCase()) : false;
};

type NewFilePreviewProps = {
    file: File;
    onRemove: () => void;
    disabled: boolean;
    globalTheme: any;
    /** 0–100 upload progress; undefined when not uploading */
    uploadProgress?: number;
};

const NewFilePreview: React.FC<NewFilePreviewProps> = ({ file, onRemove, disabled, globalTheme, uploadProgress }) => {
    const { t } = useTranslation();
    const showImagePreview = isImageFile(file);
    const [objectUrl] = useState<string | null>(() =>
        showImagePreview ? URL.createObjectURL(file) : null
    );
    const isUploading = uploadProgress !== undefined && uploadProgress < 100;

    useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    return (
        <div className="w-1/2 md:w-1/4 p-2">
            <div className="shadow-sm border overflow-hidden rounded-md" style={{backgroundColor: globalTheme.siteVariables.colorScheme.default.background}}>
                {showImagePreview && objectUrl ? (
                    <div
                        style={{
                            backgroundImage: `url(${objectUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            width: '100%',
                            aspectRatio: '16/9',
                            minHeight: '120px',
                            position: 'relative',
                        }}
                    >
                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background3,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${uploadProgress ?? 0}%`,
                                        backgroundColor: globalTheme.siteVariables.colorScheme.brand.foreground1,
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="relative flex items-center justify-center"
                        style={{ padding: '1rem', minHeight: '120px' }}
                    >
                        {getFileIcon(file.name, { file }, globalTheme)}
                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background3,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${uploadProgress ?? 0}%`,
                                        backgroundColor: globalTheme.siteVariables.colorScheme.brand.foreground1,
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div className="flex min-w-0 items-center">
                    <div className="min-w-0 flex-1 truncate px-2" title={file.name}>
                        {file.name}
                    </div>
                    <div className="shrink-0">
                        <Button
                            icon={<CloseIcon />}
                            text
                            iconOnly
                            title={t("ticket-details.edit-ticket-form.attachments.button.remove")}
                            onClick={onRemove}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

type EditableTicketChatContentProps = {
    message: string;
    setMessage: (message: string) => void;
    assignees: PlatformUser[];
    editedAttachmentsList: EditTicketAttachmentsProps[];
    setEditedAttachmentsList: (attachments: EditTicketAttachmentsProps[]) => void;
    onCancel: () => void;
    comment: any;
    handleUpdateMessage: (newFilesToUpload: File[]) => void;
    handleSendMessage: (newFilesToUpload: File[]) => void;
    isPublic: boolean;
    setIsPublic: (isPublic: boolean) => void;
    loading?: boolean;
    /** When loading, which action to display: "Saving..." or "Sending..." */
    actionStatus?: "saving" | "sending";
    /** Per-file upload progress 0–100, keyed by new-file index */
    uploadProgress?: Record<number, number>;
    hasChanges?: boolean;
};

export const EditableTicketChatContent: React.FC<EditableTicketChatContentProps> = ({
    message,
    setMessage,
    assignees,
    editedAttachmentsList,
    setEditedAttachmentsList,
    onCancel,
    comment,
    handleUpdateMessage,
    handleSendMessage,
    isPublic,
    setIsPublic,
    loading = false,
    actionStatus,
    uploadProgress = {},
    hasChanges = false,
}) => {
    const { t } = useTranslation();
    type PendingNewFile = { key: string; file: File };
    const nextNewFileKeyRef = useRef(0);
    const [newFilesToUpload, setNewFilesToUpload] = useState<PendingNewFile[]>([]);
    // What the editor showed when it opened. Typing anything - a space included - makes Save ready,
    // as it does in a new comment, even where the saved Markdown comes out the same.
    // The box's starting lines, built once: the editor reads its value only when it mounts, and the
    // opening text below has to be taken from exactly these lines, or the first keystroke would
    // read the trimmed blank line as an edit.
    const openingValue = useRef<ReturnType<typeof markdownToSlate>>();
    openingValue.current ??= withoutTrailingEmptyLines(markdownToSlate(message));
    const openingText = useRef<string>();
    openingText.current ??= editorPlainText(openingValue.current);
    const [editorText, setEditorText] = useState(openingText.current);
    const textTyped = editorText !== openingText.current;
    const canSave = message.trim() !== '' && (hasChanges || textTyped || newFilesToUpload.length > 0);
    // An internal note is saved, never sent - Send stays off while the box is ticked.
    const canSend = canSave && isPublic;
    const droppedIndexRef = useRef(0);

    const appendNewFiles = (files: File[]) => {
        setNewFilesToUpload((prev) => [
            ...prev,
            ...files.map((file) => ({
                key: `new-file-${nextNewFileKeyRef.current++}`,
                file,
            })),
        ]);
    };

    const handleImagePaste = (file: File) => {
        const result = new File(
            [file],
            `image-${droppedIndexRef.current.toString().padStart(3, "0")}.png`,
            { type: file.type }
        );
        appendNewFiles([result]);
        droppedIndexRef.current += 1;
    };

    const { isDragging, handleDragOver, handleDragEnter, handleDragLeave, handleDrop } =
        useFileDrop({
            onFilesDropped: (files: File[]) => {
                appendNewFiles(files);
            },
            disabled: loading,
        });

    const removeNewFile = (key: string) => {
        setNewFilesToUpload((prev) => prev.filter((entry) => entry.key !== key));
    };

    return (
        <ProviderConsumer
            render={(globalTheme) => {
                return (
                    <div
                        className="shadow-sm w-full min-w-0"
                        style={{
                            maxWidth: "100%",
                            backgroundColor:
                                globalTheme.siteVariables.colorScheme.default.background,
                            borderStyle: "dashed",
                            borderWidth: isDragging ? 2 : 0,
                            borderColor: isDragging
                                ? globalTheme.siteVariables.colorScheme.brand.foreground1
                                : "transparent",
                        }}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <SlateEditor
                            className="w-full min-w-0"
                            onChange={(data) => {
                                let mk = slateToComment(data);
                                setMessage(mk);
                                setEditorText(editorPlainText(data));
                            }}
                            slateValue={openingValue.current}
                            usersInMention={assignees}
                            isFocus={true}
                            noImage={true}
                            onImagePaste={handleImagePaste}
                            // Opens at least as tall as a new comment - START_LINES of its own line height
                            // plus its padding - then fits the comment and grows and shrinks with it,
                            // with no limit: it sits inside the activity, which scrolls.
                            minHeight={`calc(${START_LINES}lh + 1.5rem)`}
                            maxHeight="none"
                            disabled={loading}
                        />

                        {(editedAttachmentsList.length > 0 || newFilesToUpload.length > 0) && (
                            <div className="flex flex-wrap">
                                {editedAttachmentsList.map((attachment: EditTicketAttachmentsProps) => (
                                    <EditableTicketAttachment
                                        key={attachment.Id}
                                        {...attachment}
                                        onRemove={() => {
                                            setEditedAttachmentsList(
                                                editedAttachmentsList.filter((att) => att.Id !== attachment.Id)
                                            );
                                        }}
                                        disabled={loading}
                                    />
                                ))}
                                {newFilesToUpload.map((entry, index) => (
                                    <NewFilePreview
                                        key={entry.key}
                                        file={entry.file}
                                        onRemove={() => removeNewFile(entry.key)}
                                        disabled={loading}
                                        globalTheme={globalTheme}
                                        uploadProgress={uploadProgress[index]}
                                    />
                                ))}
                            </div>
                        )}

                        <div
                            className="flex justify-between p-2 border-t border-b"
                            style={{
                                borderBottomColor:
                                    globalTheme.siteVariables.colorScheme.brand.foreground1,
                            }}
                        >
                            <div>
                                <Checkbox 
                                    label="Internal Note" 
                                    checked={!isPublic}
                                    disabled={loading}
                                    onChange={(_e, data) => {
                                        const isChecked = data?.checked ?? false;
                                        setIsPublic(!isChecked);
                                    }}
                                />
                            </div>
                            
                            <div className="flex action-buttons">
                                {loading && actionStatus ? (
                                    <span style={{ alignSelf: "center" }}>
                                        {actionStatus === "saving" ? "Saving..." : "Sending..."}
                                    </span>
                                ) : (
                                    <>
                                        <Button
                                            icon={<CloseIcon />}
                                            text
                                            iconOnly
                                            title={t("common.buttons.cancel")}
                                            onClick={onCancel}
                                            disabled={loading}
                                        />
                                        <Button
                                            icon={<AcceptIcon />}
                                            text
                                            // Once there is something to save: a purple check on a soft brand tint,
                                            // filling with the brand purple on hover like Send beside it. It was the
                                            // same white as when disabled, and a purple check alone - even the filled
                                            // one - read faint in light mode. All from the theme.
                                            className={canSave && !loading ? `${AppCSS.EditSave} ${AppCSS.NoFade} icon-filled` : AppCSS.NoFade}
                                            style={{
                                                '--edit-save': globalTheme.siteVariables.colorScheme.brand.foreground1,
                                                '--edit-save-bg': globalTheme.siteVariables.colorScheme.brand.background1,
                                                '--edit-save-hover': globalTheme.siteVariables.colorScheme.brand.foreground4,
                                                '--edit-save-hover-bg': globalTheme.siteVariables.colorScheme.brand.background,
                                            } as React.CSSProperties}
                                            iconOnly
                                            title={t("common.buttons.save")}
                                            onClick={() =>
                                                handleUpdateMessage(newFilesToUpload.map((e) => e.file))
                                            }
                                            disabled={!canSave || loading}
                                        />
                                        <Divider vertical />
                                        {canSend && !loading ? (
                                            // Ready to send, it is the new comment's Send button - white on its
                                            // purple, darker on hover - as the folded draft's send is. Northstar's
                                            // own icon-only primary hover was a pale, translucent purple instead.
                                            <button
                                                type="button"
                                                className={AppCSS.DraftSend}
                                                title={t("ticket-details.ticket-conversation.send")}
                                                aria-label={t("ticket-details.ticket-conversation.send")}
                                                style={{
                                                    '--draft-send': globalTheme.siteVariables.colorScheme.brand.background,
                                                    '--draft-send-hover': globalTheme.siteVariables.colorScheme.brand.backgroundHover,
                                                    color: globalTheme.siteVariables.colorScheme.brand.foreground4,
                                                    alignSelf: 'center',
                                                    // The same square as Save beside it; the folded line keeps the
                                                    // class's shorter 28px so that line does not grow.
                                                    height: 32,
                                                } as React.CSSProperties}
                                                onClick={() =>
                                                    handleSendMessage(newFilesToUpload.map((e) => e.file))
                                                }
                                            >
                                                <SendIcon />
                                            </button>
                                        ) : (
                                            <Button
                                                icon={<SendIcon />}
                                                text
                                                iconOnly
                                                title={t("ticket-details.ticket-conversation.send")}
                                                disabled
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }}
        />
    );
};
